<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <base-widget
    icon="leaveAlt"
    icon-type="svg"
    :loading="isLoading"
    title="My Leave"
  >
    <div class="orangehrm-my-leave-widget">
      <div class="orangehrm-my-leave-widget__section">
        <oxd-text tag="p" class="orangehrm-my-leave-widget__section-title">
          Available Balance
        </oxd-text>
        <div
          v-if="balances.length > 0"
          class="orangehrm-my-leave-widget__balance-list"
        >
          <div
            v-for="item in balances"
            :key="item.leaveType.id"
            class="orangehrm-my-leave-widget__balance-row"
          >
            <div class="orangehrm-my-leave-widget__balance-meta">
              <oxd-text tag="p" class="orangehrm-my-leave-widget__leave-type">
                {{ item.leaveType.name }}
              </oxd-text>
              <oxd-text
                tag="p"
                class="orangehrm-my-leave-widget__balance-caption"
              >
                {{
                  isFrontLoaded(item)
                    ? 'Days left from the yearly bucket'
                    : 'Leave available'
                }}
              </oxd-text>
              <div
                v-if="isFrontLoaded(item)"
                class="orangehrm-my-leave-widget__borrowed-row"
              >
                <oxd-text
                  tag="p"
                  class="orangehrm-my-leave-widget__borrowed-label"
                >
                  Taken Days
                </oxd-text>
                <oxd-text
                  tag="p"
                  class="orangehrm-my-leave-widget__borrowed-value"
                >
                  {{ formatBorrowed(item.accrualSummary.borrowedDays) }} days
                </oxd-text>
              </div>
            </div>
            <div class="orangehrm-my-leave-widget__balance-pill">
              <span class="orangehrm-my-leave-widget__leave-balance-value">
                <template v-if="isFrontLoaded(item)">
                  {{ formatBalance(item.balance) }}/{{
                    formatBalance(item.accrualSummary.displayEntitlementDays)
                  }}
                </template>
                <template v-else>
                  {{ formatBalance(item.balance) }}
                </template>
              </span>
              <span class="orangehrm-my-leave-widget__leave-balance-unit">
                days
              </span>
            </div>
          </div>
        </div>
        <oxd-text v-else tag="p" class="orangehrm-my-leave-widget__empty-text">
          No leave balance available.
        </oxd-text>
      </div>
      <oxd-divider />
      <div class="orangehrm-my-leave-widget__section">
        <oxd-text tag="p" class="orangehrm-my-leave-widget__section-title">
          Next Scheduled Day(s) Off
        </oxd-text>
        <div class="orangehrm-my-leave-widget__next-card">
          <oxd-text tag="p" class="orangehrm-my-leave-widget__next-leave">
            {{ nextScheduledLeaveLabel }}
          </oxd-text>
        </div>
      </div>
    </div>
  </base-widget>
</template>

<script>
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@/core/util/services/api.service';
import BaseWidget from '@/orangehrmDashboardPlugin/components/BaseWidget.vue';

export default {
  name: 'MyLeaveWidget',

  components: {
    'base-widget': BaseWidget,
  },

  setup() {
    const {locale} = useLocale();
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/dashboard/employees/my-leave-summary',
    );

    return {
      http,
      locale,
    };
  },

  data() {
    return {
      balances: [],
      isLoading: false,
      nextScheduledLeave: null,
      numberFormatter: new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      }),
    };
  },

  computed: {
    nextScheduledLeaveLabel() {
      if (
        !this.nextScheduledLeave?.fromDate ||
        !this.nextScheduledLeave?.toDate
      ) {
        return 'No scheduled leave.';
      }

      const fromDate = parseDate(this.nextScheduledLeave.fromDate);
      const toDate = parseDate(this.nextScheduledLeave.toDate);
      if (!fromDate || !toDate) {
        return 'No scheduled leave.';
      }

      const formattedFromDate = formatDate(fromDate, 'EEEE, MMMM d', {
        locale: this.locale,
      });
      const formattedToDate = formatDate(toDate, 'EEEE, MMMM d', {
        locale: this.locale,
      });

      if (this.nextScheduledLeave.fromDate === this.nextScheduledLeave.toDate) {
        return formattedFromDate;
      }

      return `${formattedFromDate} - ${formattedToDate}`;
    },
  },

  beforeMount() {
    this.fetchWidgetData();
  },

  methods: {
    fetchWidgetData() {
      this.isLoading = true;
      this.http
        .getAll()
        .then((response) => {
          const {data} = response.data;
          this.balances = data?.balances ?? [];
          this.nextScheduledLeave = data?.nextScheduledLeave ?? null;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatBalance(value) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) {
        return this.numberFormatter.format(0);
      }
      return this.numberFormatter.format(numericValue);
    },
    formatBorrowed(value) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue) || numericValue <= 0) {
        return this.numberFormatter.format(0);
      }

      return `-${this.numberFormatter.format(numericValue)}`;
    },
    isFrontLoaded(item) {
      return Boolean(item?.accrualSummary?.frontLoaded);
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-my-leave-widget {
  color: var(--oxd-interface-gray-darken-1-color, #64728c);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0 0.5rem 0.25rem;

  &__section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__balance-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__balance-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 0.95rem;
    border: 1px solid $oxd-primary-one-alpha-20-color;
    border-radius: 1rem;
    background: linear-gradient(
      135deg,
      $oxd-primary-one-alpha-10-color,
      $oxd-primary-one-alpha-15-color
    );
  }

  &__balance-meta {
    min-width: 0;
  }

  &__borrowed-row {
    display: flex;
    align-items: baseline;
    gap: 0.45rem;
    margin-top: 0.45rem;
  }

  &__borrowed-label,
  &__borrowed-value {
    margin: 0;
  }

  &__borrowed-label {
    color: var(--oxd-interface-gray-darken-1-color, #64728c) !important;
    font-size: 0.78rem;
    font-weight: 700;
  }

  &__borrowed-value {
    color: var(--oxd-interface-gray-darken-2-color, #38455d) !important;
    font-size: 0.86rem;
    font-weight: 700;
  }

  &__balance-pill {
    display: inline-flex;
    align-items: baseline;
    gap: 0.35rem;
    padding: 0.45rem 0.7rem;
    border-radius: 999px;
    background-color: $oxd-white-color;
    box-shadow: 0 8px 18px rgba(23, 49, 64, 0.08);
  }

  &__leave-type,
  &__next-leave,
  &__empty-text,
  &__section-title {
    margin: 0;
  }

  &__leave-type {
    color: var(--oxd-interface-gray-darken-2-color, #38455d) !important;
    font-size: 0.95rem;
    font-weight: 700;
  }

  &__balance-caption,
  &__section-title {
    color: var(--oxd-interface-gray-color, #929baa) !important;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0;
    text-transform: uppercase;
  }

  &__leave-balance-value {
    color: var(--oxd-primary-one-color);
    font-size: 1.1rem;
    font-weight: 700;
  }

  &__leave-balance-unit {
    color: var(--oxd-interface-gray-darken-1-color, #64728c);
    font-size: 0.76rem;
    font-weight: 700;
  }

  &__next-card {
    padding: 0.9rem 0.95rem;
    border-radius: 1rem;
    background-color: $oxd-white-color;
    box-shadow: inset 0 0 0 1px $oxd-primary-one-alpha-15-color;
  }

  &__next-leave {
    color: var(--oxd-interface-gray-darken-2-color, #38455d) !important;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.5;
  }

  &__empty-text {
    color: var(--oxd-interface-gray-darken-1-color, #64728c) !important;
    font-size: 0.9rem;
    line-height: 1.5;
  }
}
</style>
